import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { inboxApi } from '@wa/shared';

/**
 * The balance, in the header.
 *
 * Every WhatsApp platform puts this here because every message costs money and an
 * agent who cannot see the balance discovers it is empty by having a send fail.
 * It also does a quieter job: a number and a call to action are what makes the top
 * of an application look like a product rather than a page title.
 *
 * Below the tenant's own low-balance threshold it turns amber and the action
 * becomes the point.
 *
 * It is also how Billing is reached, and that is deliberate. Billing was briefly a
 * sixth entry in the rail, and a sixth entry is a mobile layout change: at 360px the
 * bottom bar gives each item sixty pixels, and "Contacts" and "Campaigns" ran into
 * each other. The balance was already in the header on every screen with a button
 * that said Top up — the destination it wanted was the one being added to the rail.
 */
export function WalletChip() {
  // Refetched on a timer as well as invalidated by top-ups and launches: the
  // balance moves with every send, and this chip is mounted for the whole
  // session, so with nothing else marking it stale it showed the morning's
  // figure all day. The interval pauses in a background tab.
  const wallet = useQuery({
    queryKey: ['wallet'],
    queryFn: inboxApi.wallet,
    staleTime: 60_000,
    refetchInterval: 120_000,
  });

  if (wallet.isPending || wallet.isError) return null;

  const { balance, lowBalanceAlert } = wallet.data;
  const low = balance <= lowBalanceAlert;

  return (
    <div
      className={[
        'flex min-w-0 items-center gap-1.5 rounded-(--radius-chip) border py-1 pr-1 pl-2.5',
        low
          ? 'border-(--color-warn) bg-(--color-note)'
          : 'border-(--color-line) bg-(--color-surface)',
      ].join(' ')}
    >
      <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true"
           className={"shrink-0 " + (low ? 'text-(--color-warn)' : 'text-(--color-muted)')}>
        <path d="M3 6.5A1.5 1.5 0 0 1 4.5 5h11A1.5 1.5 0 0 1 17 6.5v7a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 3 13.5z"
              fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M13.2 10.9a.9.9 0 1 0 0-1.8.9.9 0 0 0 0 1.8z" fill="currentColor" />
      </svg>

      <span className={[
        // Allowed to truncate. On the narrowest phone this is the element with
        // slack in it; the switch beside it is not.
        'min-w-0 truncate tabular-nums text-small font-semibold',
        low ? 'text-(--color-note-strong)' : 'text-(--color-ink)',
      ].join(' ')}>
        {/* Indian grouping, because the number is rupees and 1,23,221 is how it is
            read here. Intl does this correctly for en-IN; hand-rolling it does not. */}
        {new Intl.NumberFormat('en-IN', {
          style: 'currency', currency: 'INR', maximumFractionDigits: 2,
        }).format(balance)}
      </span>

      <Link
        to="/billing"
        title="Balance, statements and invoices"
        className="tap shrink-0 rounded-(--radius-chip) bg-(--color-brand) px-2.5 py-0.5 text-micro font-semibold text-(--color-brand-ink)"
      >
        Top up
      </Link>
    </div>
  );
}
